import * as React from 'react';
import { ActionAfterViewed } from '@cian/action-after-viewed-component';
import { ERouteControlViewType } from '@cian/frontend-newbuilding-map-component';
import { getLogger } from '@cian/logger/browser';
import { useSelector } from 'react-redux';

import { Map as MapComponent } from '../../components/Map';
import { InfrastructureMapTypes, MapRouteButton, Years, YearsService } from '../../components/Map/parts';
import { ALWAYS_VISIBLE_INFRASTRUCTURE_TYPES } from '../../constants/infrastructure';
import { getMapApiKey, getMapApiVersion } from '../../selectors/common';
import { getIsDistanceCalculatingEnabled } from '../../selectors/distanceCalculating';
import { getIsMapServicesVisible } from '../../selectors/mapServices';
import { getGeoCoordinates } from '../../selectors/newbuilding';
import {
  EMapService,
  IRecommendationNewbuildingInfrastructure,
  TRegularInfrastructureItemType,
  TTAInfrastructureType,
} from '../../types/map';
import {
  trackBuildRouteClose,
  trackInfrastructureClick,
  trackRulerClick,
  trackSimilarNewbuildingInfrastructureOpen,
  trackYearClick,
} from './analytics';
import { getFirstInfrastructureType, getZoom } from './helpers';
import {
  useFullscreen,
  useGeoObjectBalloon,
  useGeoObjects,
  useInfrastructureTypesState,
  useFutureInfrastructure,
  useFutureTypes,
  useSelectedYear,
  useSetGeoObjectsCallbacks,
  useRouting,
  useMapRef,
  usePanorama,
  useInfrastructureShowHandler,
} from './hooks';
import { toRegularInfrastructureItems } from './helpers/mappers';
import { MapServicesContainer } from './parts/MapServices';
import { TransportAccessibilitySidebarContainer } from './parts/TransportAccessibilitySidebar';

export function MapContainer() {
  const pinCoordinates = useSelector(getGeoCoordinates);
  const apiKey = useSelector(getMapApiKey);
  const apiVersion = useSelector(getMapApiVersion);
  const isDistanceCalculatingEnabled = useSelector(getIsDistanceCalculatingEnabled);
  const isMapServicesVisible = useSelector(getIsMapServicesVisible);

  const mapRef = useMapRef();
  const regularInfrastructureTypeRef = React.useRef<string | undefined>();
  const TAInfrastructureTypeRef = React.useRef<string | undefined>();

  const {
    regularInfrastructureByYearRef,
    transportAccessibilityInfrastructureByTypeRef,
    regularInfrastructureTypes,
    handleGeoObjectsFirstFetch,
  } = useGeoObjects();

  const [currentTypes, setCurrentTypes] = useInfrastructureTypesState(regularInfrastructureTypes);
  const { handleSelectYear, selectedYear, nextAvailableYear } = useSelectedYear(regularInfrastructureByYearRef);
  const [futureTypes, setFutureTypes] = useFutureTypes(regularInfrastructureByYearRef);
  const hasFutureInfrastructure = useFutureInfrastructure(regularInfrastructureByYearRef);

  const { isPanoramaMode, handlePanoramaSuccessEnter, handleNoPanoramaEnter, handlePanoramaExit } = usePanorama(
    TAInfrastructureTypeRef,
    regularInfrastructureTypeRef,
    regularInfrastructureByYearRef.current,
  );

  const { isFullscreen, handleFullscreenEnter, handleFullscreenExit } = useFullscreen(
    TAInfrastructureTypeRef,
    regularInfrastructureTypeRef,
    regularInfrastructureByYearRef.current,
  );

  const [isMapLoaded, setIsMapLoaded] = React.useState(false);
  const [isRouteMode, setRouteMode] = React.useState(false);
  const [mapService, setMapService] = React.useState<EMapService | null>(null);
  const isRegularInfrastructureMode =
    !isPanoramaMode && !isRouteMode && mapService !== EMapService.TransportAccessibility;
  const isRouteButtonVisible =
    !isMapServicesVisible && isMapLoaded && isRegularInfrastructureMode && isDistanceCalculatingEnabled;
  // TODO:  Будет удалено при выпиле фича-флага в NB-1172.
  const isMapServiceMenuVisible = isMapServicesVisible && mapService === null && !isPanoramaMode;
  const isTransportAccessibilityServiceVisible = mapService === EMapService.TransportAccessibility && !isPanoramaMode;
  const isTopWarningVisible = hasFutureInfrastructure && !isMapServicesVisible;
  // TODO: Одна из переменных будет удалена при выпиле фича-флага в NB-1172.
  const yearsTimelineVisible = hasFutureInfrastructure && !isMapServicesVisible && !isPanoramaMode;
  const yearsServiceVisible =
    hasFutureInfrastructure && mapService === EMapService.FutureInfrastructure && !isPanoramaMode;
  const yearsVisible = yearsTimelineVisible || yearsServiceVisible;

  const {
    handleBalloonRouteButtonClick,
    handleRoutingClick,
    handleRoutingBuildInit,
    handleRouteBuildingSuccess,
    resetTARouting,
  } = useRouting({
    hasFutureInfrastructure,
    mapApi: mapRef.current,
    regularInfrastructureType: regularInfrastructureTypeRef.current,
    setMapService,
  });

  const { setRegularGeoObjects, setTransportAccessibilityGeoObjects } = useSetGeoObjectsCallbacks(
    mapRef.current,
    regularInfrastructureByYearRef.current,
    transportAccessibilityInfrastructureByTypeRef.current,
    currentTypes,
    selectedYear,
    setFutureTypes,
    resetTARouting,
  );

  React.useEffect(() => {
    if (isMapLoaded) {
      setRegularGeoObjects(currentTypes, selectedYear, !yearsVisible);
    }
  }, [currentTypes, selectedYear, yearsVisible]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    regularInfrastructureTypeRef.current = getFirstInfrastructureType(regularInfrastructureTypes);
  }, [regularInfrastructureTypes]);

  const handleSimilarNewbuildingClick = React.useCallback((item: IRecommendationNewbuildingInfrastructure) => {
    trackSimilarNewbuildingInfrastructureOpen(item);
  }, []);

  const { handleBalloonHtmlFirstFetch, handleBalloonOpen } = useGeoObjectBalloon({
    regularInfrastructureByYearRef,
    transportAccessibilityInfrastructureByTypeRef,
    onRouteButtonClick: handleBalloonRouteButtonClick,
    onSimilarNewbuildingClick: handleSimilarNewbuildingClick,
    resetTARouting,
    mapService,
  });

  const handleChangeInfrastructureType = React.useCallback((types: string[]) => {
    setCurrentTypes(types);

    const infrastructureType = types.find(type => !ALWAYS_VISIBLE_INFRASTRUCTURE_TYPES.includes(type));

    const infrastructureItems = toRegularInfrastructureItems(regularInfrastructureByYearRef.current);

    trackInfrastructureClick({ infrastructureItems, infrastructureType });
    regularInfrastructureTypeRef.current = infrastructureType;
  }, []);

  const handleChangeTransportAccessibilityService = React.useCallback(
    (infrastructureType: TTAInfrastructureType | null) => {
      setTransportAccessibilityGeoObjects(infrastructureType);
      TAInfrastructureTypeRef.current = infrastructureType || undefined;
    },
    [setTransportAccessibilityGeoObjects],
  );

  const handleServiceClick = React.useCallback(
    (serviceType: EMapService | null) => {
      setMapService(serviceType);

      if (serviceType === EMapService.Routes) {
        handleRoutingClick(ERouteControlViewType.stickyLeft);
      }
    },
    [handleRoutingClick],
  );

  const handleMapLoaded = React.useCallback(() => setIsMapLoaded(true), []);
  const handleRouteModeEnter = React.useCallback(() => setRouteMode(true), []);

  const handleRouteModeExit = React.useCallback(() => {
    setRouteMode(false);
    trackBuildRouteClose();

    if (isMapServicesVisible) {
      setMapService(null);
    }
  }, [isMapServicesVisible]);

  const handleYearClick = React.useCallback(
    (selectedYear: number) => {
      const selectedInfrastructureType = (regularInfrastructureTypeRef.current ||
        'metro') as TRegularInfrastructureItemType;
      trackYearClick({ year: selectedYear, objectType: selectedInfrastructureType });
      handleSelectYear(selectedYear);
    },
    [regularInfrastructureTypeRef, handleSelectYear],
  );

  const handleRulerClick = React.useCallback(() => {
    const regularInfrastructureItems = toRegularInfrastructureItems(regularInfrastructureByYearRef.current);
    trackRulerClick(
      regularInfrastructureItems,
      TAInfrastructureTypeRef.current || regularInfrastructureTypeRef.current,
    );
  }, []);

  const handleError = React.useCallback((error: Error) => {
    getLogger().error(error);
  }, []);

  const infrastructureShowHandler = useInfrastructureShowHandler(regularInfrastructureByYearRef);

  if (!pinCoordinates) {
    return null;
  }

  return (
    <ActionAfterViewed callback={infrastructureShowHandler} percentVisible={0.5}>
      <MapComponent
        apiKey={apiKey}
        apiVersion={apiVersion}
        mapRef={mapRef}
        isFullscreen={isFullscreen}
        pinCoordinates={pinCoordinates}
        currentTypes={currentTypes}
        zoom={getZoom(currentTypes.length)}
        infrastructureTypes={
          isRegularInfrastructureMode &&
          !!regularInfrastructureTypes && (
            <InfrastructureMapTypes
              types={regularInfrastructureTypes}
              futureTypes={futureTypes}
              currentTypes={currentTypes}
              onTypesChange={handleChangeInfrastructureType}
            />
          )
        }
        years={
          yearsTimelineVisible && (
            <Years
              selectedYear={selectedYear}
              nextAvailableYear={nextAvailableYear}
              onChange={handleYearClick}
              isFullscreen={isFullscreen}
            />
          )
        }
        yearsService={
          yearsServiceVisible && (
            <YearsService
              selectedYear={selectedYear}
              nextAvailableYear={nextAvailableYear}
              onChange={handleYearClick}
              onClose={() => setMapService(null)}
              isFullscreen={isFullscreen}
            />
          )
        }
        routeButton={isRouteButtonVisible ? <MapRouteButton onClick={handleRoutingClick} /> : null}
        warningText={isTopWarningVisible ? 'Данные могут быть неточными' : undefined}
        mapServices={isMapServiceMenuVisible && <MapServicesContainer onServiceClick={handleServiceClick} />}
        transportAccessibilityService={
          isTransportAccessibilityServiceVisible && (
            <TransportAccessibilitySidebarContainer
              onTransportAccessibilityChange={handleChangeTransportAccessibilityService}
              onClose={() => setMapService(null)}
            />
          )
        }
        onGeoObjectsFirstFetch={handleGeoObjectsFirstFetch}
        onBalloonHtmlFirstFetch={handleBalloonHtmlFirstFetch}
        onError={handleError}
        onFullscreenEnter={handleFullscreenEnter}
        onFullscreenExit={handleFullscreenExit}
        onPanoramaSuccessEnter={handlePanoramaSuccessEnter}
        onNoPanoramaEnter={handleNoPanoramaEnter}
        onPanoramaExit={handlePanoramaExit}
        onMapLoaded={handleMapLoaded}
        onBalloonOpen={handleBalloonOpen}
        onRoutingStart={handleRouteModeEnter}
        onRoutingEnd={handleRouteModeExit}
        onRouteBuildingInit={handleRoutingBuildInit}
        onRouteBuildingSuccess={handleRouteBuildingSuccess}
        onRulerClick={handleRulerClick}
      />
    </ActionAfterViewed>
  );
}
